import React from 'react';
import { CheckCircle, Circle, XCircle, AlertTriangle, RefreshCwIcon } from 'lucide-react';
import { OrderStatus, ORDER_STATUSES } from '@/types/order';

interface OrderStatusTimelineProps {
  currentStatus: OrderStatus;
  createdAt?: string; // ISO date string
  orientation?: 'horizontal' | 'vertical'; // Added orientation prop
}

// ... (primaryTimelineOrder, terminalStatuses, successStatuses, TimelineStep interface remain the same) ...
const primaryTimelineOrder: OrderStatus[] = [
  'pending',
  'confirmed',
  'preparing',
  'out_for_delivery',
  'delivered',
];
const terminalStatuses: OrderStatus[] = ['cancelled', 'refunded', 'failed_delivery', 'delayed'];
const successStatuses: OrderStatus[] = ['delivered', 'succeeded'];

interface TimelineStep {
  status: OrderStatus;
  label: string;
  description?: string;
  isCompleted: boolean;
  isCurrent: boolean;
  isUpcoming: boolean;
  date?: string; 
  iconNode?: React.ReactNode;
  color?: string; 
}

const getStatusConfigDefaults = (status: OrderStatus): { label: string, description?: string, color: string } => {
  const foundConfig = ORDER_STATUSES.find(s => s.status === status);
  if (foundConfig) return foundConfig;
  let label = status.replace(/_/g, ' ');
  label = label.charAt(0).toUpperCase() + label.slice(1);
  let color = 'bg-gray-500';
  if (status === 'refunded') color = 'bg-red-500';
  if (status === 'succeeded') color = 'bg-green-500';
  return { label, description: `Status: ${label}`, color };
};

export const OrderStatusTimeline: React.FC<OrderStatusTimelineProps> = ({
  currentStatus,
  createdAt,
  orientation = 'vertical', // Default to vertical
}) => {
  // ... (steps logic remains the same) ...
  const steps: TimelineStep[] = [];
  let effectiveCurrentStatus = currentStatus;
  if (currentStatus === 'succeeded') effectiveCurrentStatus = 'delivered';
  const effectiveCurrentStatusIndex = primaryTimelineOrder.indexOf(effectiveCurrentStatus);

  primaryTimelineOrder.forEach((statusVal, index) => {
    const config = getStatusConfigDefaults(statusVal);
    let isCompleted = false;
    if (successStatuses.includes(effectiveCurrentStatus)) {
      isCompleted = primaryTimelineOrder.indexOf(statusVal) < effectiveCurrentStatusIndex;
    } else if (effectiveCurrentStatusIndex !== -1) {
      isCompleted = index < effectiveCurrentStatusIndex;
    }
    const isCurrent = statusVal === effectiveCurrentStatus && !terminalStatuses.includes(effectiveCurrentStatus);
    const isUpcoming = effectiveCurrentStatusIndex !== -1 && index > effectiveCurrentStatusIndex && !terminalStatuses.includes(effectiveCurrentStatus);
    steps.push({
      status: statusVal, label: config.label, description: config.description,
      isCompleted, isCurrent, isUpcoming,
      date: statusVal === 'pending' && createdAt ? new Date(createdAt).toLocaleDateString() : undefined,
      color: config.color,
    });
  });

  if (terminalStatuses.includes(currentStatus) || 
      (!primaryTimelineOrder.includes(currentStatus) && !successStatuses.includes(currentStatus))) {
    steps.length = 0;
    const config = getStatusConfigDefaults(currentStatus);
    steps.push({
      status: currentStatus, label: config.label, description: config.description,
      isCompleted: false, isCurrent: true, isUpcoming: false,
      date: createdAt ? new Date(createdAt).toLocaleDateString() : undefined,
      color: config.color,
    });
  }

  const isHorizontal = orientation === 'horizontal';

  return (
    <div className={`flex ${isHorizontal ? 'flex-row space-x-2 overflow-x-auto py-2' : 'flex-col space-y-2' }`}>
      {steps.map((step, index) => {
        // ... (iconNode, iconColorClass, stepBgClass, textColorClass, lineBgClass logic mostly same) ...
        let iconNode: React.ReactNode;
        const iconColorClass = 'text-white'; 
        let stepBgClass = step.color || 'bg-gray-300';
        let textColorClass = 'text-gray-700';
        let lineBgClass = 'bg-gray-300';

        if (step.isCompleted) {
          iconNode = <CheckCircle className={`h-5 w-5 ${iconColorClass}`} />;
          lineBgClass = stepBgClass;
          textColorClass = `text-gray-800`;
        } else if (step.isCurrent) {
          stepBgClass = step.color || 'bg-blue-500';
          textColorClass = `font-semibold`; // Color applied by step.color on parent for horizontal
          if (terminalStatuses.includes(step.status) && step.status !== 'delayed') {
            iconNode = <XCircle className={`h-5 w-5 ${iconColorClass}`} />;
          } else if (step.status === 'delayed') {
            iconNode = <AlertTriangle className={`h-5 w-5 ${iconColorClass}`} />;
          } else {
            iconNode = <RefreshCwIcon className={`h-5 w-5 ${iconColorClass} ${isHorizontal ? '' : 'animate-pulse'}`} />;
          }
        } else { // Upcoming
          iconNode = <Circle className={`h-5 w-5 text-gray-500`} />;
          stepBgClass = 'bg-gray-300';
          textColorClass = 'text-gray-500';
        }
        // For horizontal, apply text color based on status color directly
        if (isHorizontal && step.isCurrent) {
            const colorName = step.color?.replace('bg-', '').split('-')[0];
            textColorClass = `font-semibold text-${colorName}-600`;
        }
        if (isHorizontal && step.isCompleted) textColorClass = 'text-green-600';

        return (
          <div key={step.status} className={`flex ${isHorizontal ? 'flex-col items-center text-center' : 'items-start'}`}>
            <div className={`flex ${isHorizontal ? 'flex-col items-center' : 'items-center mr-4 rtl:mr-0 rtl:ml-4'}`}>
              <div className={`rounded-full h-8 w-8 flex items-center justify-center ${stepBgClass}`}>
                {iconNode}
              </div>
              {/* Connector Line - adjusted for horizontal/vertical */}
              {!isHorizontal && index < steps.length - 1 && (
                <div className={`w-0.5 grow ${step.isCompleted ? lineBgClass : 'bg-gray-300'}`} style={{ minHeight: '1.5rem' }}></div>
              )}
              {isHorizontal && index < steps.length - 1 && (
                 <div className={`h-0.5 grow ${step.isCompleted ? lineBgClass : 'bg-gray-300'} ${isHorizontal ? 'w-16 m-0 p-0 self-center relative left-1/2 top-[-1.5rem] ' : ''}`} ></div>              
              )}
            </div>
            <div className={`${isHorizontal ? 'mt-1 text-xs w-20 break-words' : `pt-1 ${textColorClass}`}`}>
              <p className={`font-medium ${isHorizontal ? textColorClass : ''}`}>{step.label}</p>
              {!isHorizontal && step.description && <p className="text-xs ">{step.description}</p>}
              {/* Optionally show date for horizontal too, if desired */}
              {!isHorizontal && step.date && <p className="text-xs text-gray-400">{step.date}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrderStatusTimeline;
 